import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Table as AntTable } from "antd";
import Table from "../../../core/common/dataTable/index";
import { useStudentTransfers } from "./hooks/useStudentTransfers";
import { all_routes } from "../../router/all_routes";
import { useCenter } from "../centers/hooks/useCenter";
import CommonSelect from "../../../core/common/commonSelect";
import StudentTransferModals from "./studentTransferModals";
import { formatDateToYMD } from "../../../core/utils/dateFormatter";

const StudentTransfers = () => {
  const routes = all_routes;
  const {
    transferType,
    setTransferType,
    centerId,
    setCenterId,
    page,
    setPage,
    size,
    setSize,
    transfers,
    totalItems,
    isLoading,
    isFetching,
    handleRevert,
    isReverting,
    userRole,
    currentUser,
  } = useStudentTransfers();

  const [showRevertModal, setShowRevertModal] = useState(false);
  const [selectedTransferId, setSelectedTransferId] = useState<number | null>(null);

  const confirmRevert = () => {
    if (selectedTransferId) {
      handleRevert(selectedTransferId);
      setShowRevertModal(false);
      setSelectedTransferId(null);
    }
  };

  const { useCenterList } = useCenter({ skip: true });
  // Fetch centers unconditionally to populate dropdown and resolve center names for the Type column
  const { data: centerResponse } = useCenterList({ skip: false });
  const centers = centerResponse?.data?.items || [];
  
  let viewedCenterName = "";
  if (userRole === "superadmin" && centerId) {
    viewedCenterName = centers.find((c: any) => c.id.toString() === centerId)?.name || "";
  } else if (userRole !== "superadmin" && currentUser?.center_id) {
    viewedCenterName = centers.find((c: any) => c.id === currentUser.center_id)?.name || "";
  }

  const columns = [
    {
      title: "S. No",
      render: (_: any, __: any, index: number) => index + 1 + (page - 1) * size,
    },
    {
      title: "Student Name",
      dataIndex: "student",
      render: (student: string) => student || "-",
    },
    {
      title: "From Center",
      dataIndex: "from_center",
      render: (center: string) => center || "-",
    },
    {
      title: "To Center",
      dataIndex: "to_center",
      render: (center: string) => center || "-",
    },
    {
      title: "Type",
      render: (record: any) => {
        if (transferType === "incoming") return <span className="badge badge-soft-success">Incoming</span>;
        if (transferType === "outgoing") return <span className="badge badge-soft-warning">Outgoing</span>;
        
        if (viewedCenterName) {
           if (record.from_center === viewedCenterName) return <span className="badge badge-soft-warning">Outgoing</span>;
           if (record.to_center === viewedCenterName) return <span className="badge badge-soft-success">Incoming</span>;
        }
        
        return <span className="badge badge-soft-primary">Transfer</span>;
      }
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
    },
    {
      title: "Date",
      dataIndex: "created_at",
      render: (date: string) => formatDateToYMD(date),
    },
    {
      title: "Action",
      render: (record: any) => (
        transferType === "outgoing" ? (
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedTransferId(record.id);
                      setShowRevertModal(true);
                    }}
                  >
                    <i className="ti ti-arrow-back-up me-2" />
                    Revert Transfer
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        ) : (
          "-"
        )
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Student Transfers</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Student Transfers
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">Transfers List</h4>
          </div>
          <div className="card-body pb-0">
            {/* Top Filters Section */}
            <div className="row mb-4">
              {userRole === "superadmin" && (
                <div className="col-12 col-md mb-3">
                  <label className="form-label">Select Center</label>
                  <CommonSelect
                    className="select"
                    options={centers.map((center: any) => ({
                      value: center.id.toString(),
                      label: center.name,
                    }))}
                    value={
                      centerId 
                        ? { value: centerId, label: centers.find((c: any) => c.id.toString() === centerId)?.name || "" }
                        : null
                    }
                    onChange={(option: any) => {
                      setCenterId(option?.value);
                      setPage(1);
                    }}
                    placeholder="Select Center"
                  />
                </div>
              )}
              <div className="col-12 col-md mb-3">
                <label className="form-label">Transfer Type</label>
                <CommonSelect
                  className="select"
                  options={[
                    { value: "outgoing", label: "Outgoing" },
                    { value: "incoming", label: "Incoming" },
                  ]}
                  value={{
                    value: transferType,
                    label: transferType === "incoming" ? "Incoming" : "Outgoing"
                  }}
                  onChange={(option: any) => {
                    setTransferType(option?.value || "outgoing");
                    setPage(1);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="card-body p-0 py-3">
            {/* Table Section */}
            {userRole === "superadmin" && !centerId ? (
              <div className="text-center py-5">
                <i className="ti ti-filter fs-1 text-muted mb-3"></i>
                <h4>Please select all filters to view the list</h4>
                <p className="text-warning">Missing filters: Center</p>
              </div>
            ) : (
              <div className="table-responsive">
                {isLoading || isFetching ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <Table
                    dataSource={transfers}
                    columns={columns}
                    Selection={false}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <StudentTransferModals
        showRevertModal={showRevertModal}
        setShowRevertModal={setShowRevertModal}
        confirmRevert={confirmRevert}
        isReverting={isReverting}
      />
    </div>
  );
};

export default StudentTransfers;
